import { Type, computed, inject } from '@angular/core';
import { Ability } from '@kinet-open-network/client/auth/data-access';
import {
  ApiPaginatedResponse,
  ApiQuery,
  BaseApiService,
} from '@kinet-open-network/client/core/api-client';
import {
  BaseModel,
  flattenModel,
} from '@kinet-open-network/client/shared/data-access/model';
import {
  AbilitySubject,
  EnumOptions,
} from '@kinet-open-network/shared/data-access/model';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  Observable,
  debounceTime,
  firstValueFrom,
  pipe,
  switchMap,
  tap,
} from 'rxjs';
import {
  CallStateSlice,
  setError,
  setLoaded,
  setLoading,
  withCallState,
} from './call-state.feature';
import { WithQueryConfig, withQuery } from './query.feature';

export function withPaginated<T extends BaseModel<unknown, AbilitySubject>>() {
  return signalStoreFeature(
    withState<{ paginated: ApiPaginatedResponse<T> | null }>({
      paginated: null,
    }),
    withComputed(({ paginated }) => ({
      meta: computed(() => paginated()?.meta),
      data: computed(() => paginated()?.data ?? []),
    })),
  );
}

export function withQueryLoad<T extends BaseModel<unknown, AbilitySubject>>(
  Service: Type<{
    query(query?: ApiQuery | null): Observable<ApiPaginatedResponse<T>>;
    model: Type<T>;
  }>,
) {
  return signalStoreFeature(
    {
      state: type<
        {
          query: ApiQuery | null;
          paginated: ApiPaginatedResponse<T> | null;
        } & CallStateSlice
      >(),
    },
    withMethods((store, service = inject(Service)) => ({
      loadByQuery: rxMethod<ApiQuery | null>(
        pipe(
          debounceTime(100),
          tap(() => patchState(store, setLoading())),
          switchMap((query) => service.query(query)),
          tap({
            next: (paginated) => patchState(store, { paginated }, setLoaded()),
            error: (error) => patchState(store, setError(error.message)),
          }),
        ),
      ),
    })),
    withMethods((store) => ({
      refreshPaginated: async () => {
        const query = store.query();
        if (query) {
          store.loadByQuery(query);
        }
      },
    })),
    withHooks({
      onInit(
        { query, loadByQuery },
        ability = inject(Ability),
        service = inject(Service),
      ) {
        const { model } = service;
        const subject = new model().kind;

        if (ability.can('read', subject)) {
          loadByQuery(query);
        } else {
          console.warn(
            `Trying to load data without 'read' ability for subject type '${subject}'`,
          );
        }
      },
    }),
  );
}

export function withSelectedId() {
  return signalStoreFeature(
    withState<{ selectedId: string | null }>({
      selectedId: null,
    }),
    withMethods((store) => ({
      setSelectedId: (selectedId: string | null) => {
        patchState(store, { selectedId });
      },
    })),
  );
}

export type ModelSlice<T extends BaseModel> = {
  model: T | null;
};

export function withModel<T extends BaseModel>() {
  return signalStoreFeature(
    withState<ModelSlice<T>>({
      model: null,
    }),
    withComputed(({ model }) => ({
      flatModel: computed(() => flattenModel(model())),
      modelId: computed(() => model()?.id),
    })),
    withMethods((store) => ({
      setModel: (model: T | null) => {
        patchState(store, { model });
      },
    })),
  );
}

export function withModelCreate<T extends BaseModel>(
  Service: Type<{
    create(data: Partial<T>): Observable<T>;
  }>,
) {
  return signalStoreFeature(
    {
      state: type<ModelSlice<T> & CallStateSlice>(),
    },
    withMethods((store, service = inject(Service)) => ({
      create: async (data: Partial<T>) => {
        patchState(store, setLoading());
        const created = await firstValueFrom(
          service.create(data).pipe(
            tapResponse({
              next: (created) =>
                patchState(store, { model: created }, setLoaded()),
              error: (err) => patchState(store, setError(err)),
            }),
          ),
        );

        return created;
      },
    })),
  );
}

export function withModelUpdate<T extends BaseModel, K extends keyof T = 'id'>(
  Service: Type<{
    update(id: T[K], data: Partial<T>): Observable<T>;
  }>,
) {
  return signalStoreFeature(
    {
      state: type<ModelSlice<T> & CallStateSlice>(),
    },
    withMethods((store, service = inject(Service)) => ({
      update: async (id: T[K], data: Partial<T>) => {
        patchState(store, setLoading());
        const updated = await firstValueFrom(
          service.update(id, data).pipe(
            tapResponse({
              next: (updated) =>
                patchState(store, { model: updated }, setLoaded()),
              error: (err) => patchState(store, setError(err)),
            }),
          ),
        );

        return updated;
      },
    })),
  );
}

export function withModelDelete<T extends BaseModel, K extends keyof T = 'id'>(
  Service: Type<{
    delete(id: T[K]): Observable<boolean>;
  }>,
) {
  return signalStoreFeature(
    {
      state: type<ModelSlice<T> & CallStateSlice>(),
    },
    withMethods((store, service = inject(Service)) => ({
      delete: async (id: T[K]) => {
        patchState(store, setLoading());
        await firstValueFrom(
          service.delete(id).pipe(
            tapResponse({
              next: () => patchState(store, { model: null }, setLoaded()),
              error: (err) => patchState(store, setError(err)),
            }),
          ),
        );
      },
    })),
  );
}

export function withModelRead<T extends BaseModel, K extends keyof T = 'id'>(
  Service: Type<{
    findOne(id: T[K]): Observable<T>;
  }>,
) {
  return signalStoreFeature(
    {
      state: type<ModelSlice<T> & CallStateSlice>(),
    },
    withMethods((store, service = inject(Service)) => ({
      load: async (id?: T[K] | null) => {
        if (!id) {
          patchState(store, { model: null }, setLoaded());
          return null;
        }
        patchState(store, setLoading());
        const entity = await firstValueFrom(
          service.findOne(id).pipe(
            tapResponse({
              next: (entity) =>
                patchState(store, { model: entity }, setLoaded()),
              error: (err) => patchState(store, setError(err)),
            }),
          ),
        );

        return entity;
      },
    })),
  );
}

export function withModelCrud<T extends BaseModel, K extends keyof T = 'id'>(
  Service: Type<BaseApiService<T, K>>,
) {
  return signalStoreFeature(
    {
      state: type<ModelSlice<T> & CallStateSlice>(),
    },
    withModelCreate(Service),
    withModelRead<T, K>(Service),
    withModelUpdate<T, K>(Service),
    withModelDelete<T, K>(Service),
  );
}

export interface ResourceStateConfig {
  query?: WithQueryConfig;
}

export function withResourceState<
  T extends BaseModel,
  K extends keyof T = 'id',
>(Service: Type<BaseApiService<T, K>>, config?: ResourceStateConfig) {
  return signalStoreFeature(
    withCallState(),
    withModel<T>(),
    withPaginated<T>(),
    withQuery(config?.query),
    withQueryLoad(Service),
    withModelCrud<T, K>(Service),
  );
}

export function withResourcePickerState<
  T extends BaseModel,
  Label extends keyof T & string,
  Value extends keyof T & string,
>(
  Service: Type<BaseApiService<T>>,
  config: {
    label: Label;
    value: Value;
    additionalFields?: (keyof T & string)[];
  },
) {
  return signalStoreFeature(
    withCallState(),
    withPaginated<T>(),
    withQuery({
      initial: {
        sort: [config.label],
        perPage: -1,
        fields: [
          config.label,
          config.value,
          ...(config?.additionalFields || []),
        ],
      },
    }),
    withQueryLoad(Service),
    withComputed(({ data }) => ({
      options: computed(() => {
        const d = data().map((model) => ({
          label: model?.[config.label] as T[Label],
          value: model?.[config.value] as T[Value],
        }));

        return d;
      }),
      optionsMap: computed(() => {
        const d = data().reduce((map, model) => {
          map.set(model?.[config.value] as T[Value], {
            label: model?.[config.label] as string,
          });
          return map;
        }, new Map<T[Value], EnumOptions>());
        return d;
      }),
    })),
    withMethods((store) => ({
      search: (term: string | null | undefined) => {
        patchState(store, {
          query: {
            ...store.query(),
            search: term ?? undefined,
          },
        });
      },
      load: () => {
        if (store.data().length > 0) {
          return;
        }

        store.refreshPaginated();
      },
    })),
  );
}
